import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "../css/LotteryPromotion.css";

// API base
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");

const OPTIONS = [
  { key: "purchase", label: "Buy Ticket", pool: 100 },
  { key: "scratch", label: "Scratch Ticket", pool: 50 },
  { key: "bill", label: "Bill Paid", pool: 50 },
  { key: "topup", label: "Top-Up ≥ $3,000", pool: 50 },
  { key: "spend", label: "Spend ≥ $5,000", pool: 50 },
  { key: "moneygram", label: "MoneyGram", pool: 50 }
];

export default function LotteryPromotion() {
  const [qty, setQty] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState("");
const [searchResult, setSearchResult] = useState(null);
const [searching, setSearching] = useState(false);
const [showDrawModal, setShowDrawModal] = useState(false);
const [selectedDraw, setSelectedDraw] = useState("");
const [drawResult, setDrawResult] = useState(null);



  /* =========================
     Quantity Control
  ========================= */
  const changeQty = (key, delta) => {
    setQty(q => {
      const current = q[key] || 0;
      const next = Math.min(10, Math.max(0, current + delta));
      return { ...q, [key]: next };
    });
  };

  /* =========================
     Load Pool / Draw Summary
  ========================= */
const loadSummary = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/lottery/summary`);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Failed to load lottery summary");
    }

    setSummary(data);
  } catch (err) {
    console.error("Lottery summary error:", err.message);
    toast.error("Lottery system unavailable");
  }
};


  useEffect(() => {
    loadSummary();
  }, []);


  
  /* =========================
     Print Ticket
  ========================= */
const printMultiSelectionTicket = (ticket) => {
  if (!ticket || !Array.isArray(ticket.selections)) {
    toast.error("Ticket data incomplete — cannot print");
    console.error("Invalid ticket payload:", ticket);
    return;
  }
  const win = window.open("", "_blank", "width=320,height=700");

  const selectionsHTML = ticket.selections
    .map(
      (s, idx) => `
        <div class="selection">
          ${s.numbers.map(n => String(n).padStart(2, "0")).join("  ")}
          <span class="bonus">(${s.bonus})</span>
        </div>
      `
    )
    .join("");

  win.document.write(`
    <html>
      <head>
        <title>Lottery Ticket</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>

        <style>
          @page { size: 80mm auto; margin: 0; }
          body {
            font-family: Arial;
            width: 80mm;
            margin: 0 auto;
            padding: 6mm;
            text-align: center;
          }
          h3 { margin: 4px 0; }
          .meta { font-size: 11px; margin-bottom: 6px; }
          .selection {
            font-size: 16px;
            margin: 4px 0;
            letter-spacing: 1px;
          }
          .bonus {
            font-size: 12px;
            margin-left: 6px;
          }
          .barcode {
            margin-top: 8px;
          }
          .footer {
            font-size: 10px;
            margin-top: 6px;
          }
        </style>
      </head>

      <body>
        <h3>RCS LOTTERY</h3>

        <div class="meta">
          Draw: ${new Date(ticket.draw_date).toDateString()}<br/>
          Ticket ID: ${ticket.id}
        </div>

        ${selectionsHTML}

        <svg id="barcode"></svg>

        <div class="footer">
          Valid for one draw only
        </div>

        <script>
          JsBarcode("#barcode", "${ticket.id}", {
            format: "CODE128",
            width: 2,
            height: 40,
            displayValue: false
          });

          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          };
        </script>
      </body>
    </html>
  `);

  win.document.close();
};



  /* =========================
     Print & Issue Logic
  ========================= */
  const printAndIssue = async (source, quantity) => {
    if (!quantity) return;

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/lottery/issue-tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source, quantity })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");

      printMultiSelectionTicket(data.ticket);


      // Reset only this counter
      setQty(q => ({ ...q, [source]: 0 }));

      loadSummary();
      toast.success(`${quantity} ticket(s) printed`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchTicket = async () => {
  if (!searchId) return;

  setSearching(true);
  setSearchResult(null);

  try {
    const res = await fetch(
      `${API_BASE}/api/lottery/ticket/${searchId}`
    );

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Ticket not found");

    setSearchResult(data);
  } catch (err) {
    toast.error(err.message);
  } finally {
    setSearching(false);
  }
};

  const printDrawResults = () => {
  const content = document.getElementById("draw-print")?.innerHTML;
  if (!content) return;

  const win = window.open("", "_blank");

  win.document.write(`
    <html>
      <head>
        <title>Draw Results</title>
        <style>
          @page { size: 80mm auto; margin: 4mm; }
          body { font-family: Arial; width: 80mm; }
        </style>
      </head>
      <body onload="window.print()">
        ${content}
      </body>
    </html>
  `);

  win.document.close();
};


  /* =========================
     UI
  ========================= */
return (
  <div className="lottery-page">
    <h2 className="lottery-title">🎟️ Lottery Promotion</h2>

{summary && (
  <div className="lottery-summary">
    <div className="summary-item">
      <span>💰 Current Pool</span>
      <strong>
        ${Number(summary.pool || 0).toLocaleString()}
      </strong>
    </div>

    <div className="summary-item">
      <span>📅 Next Draw</span>
      <strong>
        {summary.draw_date
          ? new Date(summary.draw_date).toDateString()
          : "—"}
      </strong>
    </div>

    <div className="summary-item highlight">
      <span>🎯 Potential Prize</span>
      <strong>
        ${Number(summary.potential_prize || 0).toLocaleString()}
      </strong>
    </div>
  </div>
)}


<button
  className="draw-btn"
  onClick={() => setShowDrawModal(true)}
>
  🎯 Run Draw
</button>


    <div className="lottery-options">
      {OPTIONS.map(opt => {
        const q = qty[opt.key] || 0;

        return (
          <div key={opt.key} className="lottery-card">
            <div className="lottery-left">
              <h3>{opt.label}</h3>

              <p className="price">
                ${opt.pool} <span>per ticket</span>
              </p>

              <p className="pool">
                Adds <strong>${opt.pool * q}</strong> to pool
              </p>
            </div>

            <div className="lottery-right">
              <div className="qty-control">
                <button onClick={() => changeQty(opt.key, -1)}>-</button>
                <span>{q}</span>
                <button onClick={() => changeQty(opt.key, 1)}>+</button>
              </div>

              {q > 0 && (
                <button
                  className="print-btn"
                  disabled={loading}
                  onClick={() => printAndIssue(opt.key, q)}
                >
                  🖨️ Print
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
    <div className="lottery-ticket-section">
  <h3>🔍 Ticket Lookup</h3>

  <div className="ticket-search">
    <input
      type="text"
      placeholder="Scan or enter ticket ID"
      value={searchId}
      onChange={(e) => setSearchId(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && searchTicket()}
    />
    <button onClick={searchTicket} disabled={searching}>
      Search
    </button>
  </div>

{searchResult && (() => {
  const { ticket, status, prizes } = searchResult;


  return (
    <div className={`ticket-result ${status === "winner" ? "winner" : ""}`}>
      <p><strong>Ticket ID:</strong> {ticket.id}</p>
      <p>
        <strong>Draw Date:</strong>{" "}
        {new Date(ticket.draw_date).toDateString()}
      </p>

<p><strong>Selections:</strong></p>

{ticket.selections.map((s, idx) => {
  const win = prizes?.find(p => p.selection_index === idx);

  return (
    <p key={idx}>
      Line {idx + 1}: {s.numbers.join(" - ")} (Bonus {s.bonus})
      {win && (
        <strong style={{ color: "green" }}>
          {" "}→ WIN (${win.amount.toLocaleString()})
        </strong>
      )}
    </p>
  );
})}


      <p className="status">
        {status === "pending" && "⏳ Ticket not drawn yet"}
        {status === "lost" && "❌ Not a winning ticket"}
        {status === "claimed" && "✅ Prize already claimed"}
        {status === "winner" && "🏆 WINNING TICKET"}
      </p>

{status === "winner" && (
  <button
    className="pay-btn"
    onClick={async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/lottery/claim-prizes`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ticket_id: ticket.id
            })
          }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error);

        toast.success(`Paid $${data.total.toLocaleString()}`);

        setSearchResult({
          ...searchResult,
          status: "claimed"
        });

        loadSummary();
      } catch (err) {
        toast.error(err.message);
      }
    }}
  >
    💵 Pay All Prizes
  </button>
)}

    </div>
  );
})()}


{showDrawModal && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>Run Lottery Draw</h3>

      <select
        value={selectedDraw}
        onChange={(e) => setSelectedDraw(e.target.value)}
      >
        <option value="">Select draw date</option>
        <option value="2026-01-31">Jan 31 – Weekly</option>
        <option value="2026-02-07">Feb 7 – Weekly</option>
        <option value="2026-02-14">Feb 14 – Grand</option>
      </select>

      <div className="modal-actions">
        <button onClick={() => setShowDrawModal(false)}>Cancel</button>
        <button
          disabled={!selectedDraw}
          onClick={async () => {
            try {
              const res = await fetch(
                `${API_BASE}/api/lottery/run-draw`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ draw_date: selectedDraw })
                }
              );

              const data = await res.json();
              if (!res.ok) throw new Error(data.error);

              setDrawResult(data);
              setShowDrawModal(false);
              toast.success("Draw completed");
            } catch (err) {
              toast.error(err.message);
            }
          }}
        >
          Run Draw
        </button>
      </div>
    </div>
  </div>
)}

{drawResult && (
  <div id="draw-print" className="draw-result">

    <h3>🎉 Draw Results</h3>

    <p>
      <strong>Winning Numbers:</strong>{" "}
      {drawResult.winning_numbers.join(" - ")}
    </p>

    <p>
      <strong>Pool Allocated:</strong>{" "}
      ${drawResult.allocation.toLocaleString()}

    </p>

    <p>
      <strong>Winners:</strong> {drawResult.winners.length}
    </p>

 <button onClick={printDrawResults}>
  🖨️ Print Winners (80mm)
</button>


    <button
      onClick={() =>
        window.open(
          `${API_BASE}/api/lottery/draw-report/${drawResult.draw_date}`
        )
      }
    >
      📄 Download PDF Report
    </button>
  </div>
)}


</div>

  </div>
);

}
